import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, DollarSign, GraduationCap, Layers, Building2 } from 'lucide-react';
import {
  useCreateTuitionGridBulk,
  useUpdateTuitionGrid,
  useFilieresForTuition,
  useCampusInfo,
} from '../../hooks/useTuitionGrids';
import type {
  TuitionGridWithRelations,
  BulkCreateTuitionGridData,
  CombinedPrice,
  TypeFormation,
  TuitionGridSettings,
} from '@/types/tuition.types';

interface TuitionGridFormModalProps {
  grid?: TuitionGridWithRelations | null; // If provided, edit mode (single grid)
  existingGrids?: TuitionGridWithRelations[]; // All existing grids for this filière (for pre-population)
  preselectedFiliereId?: string; // If provided, filière is pre-selected and dropdown hidden
  preselectedFiliereName?: string; // Display name for pre-selected filière
  preselectedCampusId?: string; // If provided, campus is pre-selected from filter
  settings?: TuitionGridSettings | null; // Global settings from Paramètres
  onClose: () => void;
  onSuccess: () => void;
}

const TuitionGridFormModal: React.FC<TuitionGridFormModalProps> = ({
  grid,
  existingGrids,
  preselectedFiliereId,
  preselectedFiliereName,
  preselectedCampusId,
  settings,
  onClose,
  onSuccess,
}) => {
  // Single grid edit mode = editing one specific grid (e.g., Formation Initiale only)
  // Bulk edit mode = editing/creating all grids for a filière (shows all options based on settings)
  const isSingleGridEdit = !!grid;
  const isBulkEditMode = !grid && existingGrids && existingGrids.length > 0;
  const isEditMode = isSingleGridEdit || isBulkEditMode;
  const hasPreselectedFiliere = !!preselectedFiliereId;

  // Get pricing dimensions from global settings (default to false = single price)
  const differsByNiveau = settings?.differsByNiveau || false;
  const differsByTypeFormation = settings?.differsByTypeFormation || false;
  const differsByCampus = settings?.differsByCampus || false;

  // Data queries
  const { data: filieres, isLoading: isLoadingFilieres } = useFilieresForTuition();
  const { data: campusInfo, isLoading: isLoadingCampusInfo } = useCampusInfo();

  // Mutations
  const createBulkMutation = useCreateTuitionGridBulk();
  const updateMutation = useUpdateTuitionGrid();

  // Form state - Filière selection (use preselected if provided)
  const [filiereId, setFiliereId] = useState(preselectedFiliereId || grid?.filiereId || '');

  // Form state - Campus selection (when differsByCampus is enabled)
  const [selectedCampusId, setSelectedCampusId] = useState<string | undefined>(
    preselectedCampusId || grid?.campusId || undefined
  );

  // Form state - Single price (when no dimensions vary)
  const [tuitionAmount, setTuitionAmount] = useState(grid?.tuitionAmount || 0);
  const [registrationFee, setRegistrationFee] = useState(grid?.registrationFee || 0);

  // Form state - Per-niveau prices (when differsByNiveau = true only)
  const [niveauPrices, setNiveauPrices] = useState<Record<number, { tuition: number; registration: number }>>({});

  // Form state - Per-type prices (when differsByTypeFormation = true only)
  const [typePrices, setTypePrices] = useState<{ INITIALE: { tuition: number; registration: number }; CONTINUE: { tuition: number; registration: number } }>({
    INITIALE: { tuition: 0, registration: 0 },
    CONTINUE: { tuition: 0, registration: 0 },
  });

  // Form state - Combined niveau × type prices (when BOTH are true)
  // Key format: "niveau-typeFormation" e.g., "1-INITIALE", "2-CONTINUE"
  const [combinedPrices, setCombinedPrices] = useState<Record<string, { tuition: number; registration: number }>>({});

  // Derived values
  const selectedFiliere = useMemo(
    () => filieres?.find((f) => f.id === filiereId),
    [filieres, filiereId]
  );
  const niveauOptions = selectedFiliere
    ? Array.from({ length: selectedFiliere.nombreAnnees }, (_, i) => i + 1)
    : [];

  // Initialize prices when filière changes or pre-populate from existing grids
  useEffect(() => {
    if (!selectedFiliere) return;

    // Filter existing grids by campus when differsByCampus is enabled
    let gridsToUse = existingGrids || [];
    if (differsByCampus && selectedCampusId) {
      gridsToUse = gridsToUse.filter((g) => g.campusId === selectedCampusId);
    } else if (differsByCampus && !selectedCampusId) {
      // If campus varies but none selected, filter to null-campus grids (or show empty)
      gridsToUse = gridsToUse.filter((g) => g.campusId === null);
    }
    const hasExistingData = gridsToUse.length > 0;

    // Initialize niveau prices - prefer grids with actual prices
    const initialNiveauPrices: Record<number, { tuition: number; registration: number }> = {};
    for (let i = 1; i <= selectedFiliere.nombreAnnees; i++) {
      // Try to find existing grids for this niveau, prefer one with actual price
      const niveauGrids = gridsToUse.filter((g) => g.niveau === i);
      const existingGrid = niveauGrids.find(g => g.tuitionAmount > 0 || g.registrationFee > 0) || niveauGrids[0];
      initialNiveauPrices[i] = {
        tuition: existingGrid?.tuitionAmount || 0,
        registration: existingGrid?.registrationFee || 0,
      };
    }
    setNiveauPrices(initialNiveauPrices);

    // Initialize type prices from existing data - prefer grids with actual prices
    if (hasExistingData) {
      // Find the best grid for each type (prefer one with price > 0)
      const initialeGrids = gridsToUse.filter((g) => g.typeFormation === 'INITIALE');
      const continueGrids = gridsToUse.filter((g) => g.typeFormation === 'CONTINUE');

      const initialeGrid = initialeGrids.find(g => g.tuitionAmount > 0 || g.registrationFee > 0) || initialeGrids[0];
      const continueGrid = continueGrids.find(g => g.tuitionAmount > 0 || g.registrationFee > 0) || continueGrids[0];

      setTypePrices({
        INITIALE: {
          tuition: initialeGrid?.tuitionAmount || 0,
          registration: initialeGrid?.registrationFee || 0,
        },
        CONTINUE: {
          tuition: continueGrid?.tuitionAmount || 0,
          registration: continueGrid?.registrationFee || 0,
        },
      });

      // Set single price from a grid with actual values (prefer one with price > 0)
      const gridWithPrice = gridsToUse.find(g => g.tuitionAmount > 0 || g.registrationFee > 0) || gridsToUse[0];
      if (gridWithPrice) {
        setTuitionAmount(gridWithPrice.tuitionAmount);
        setRegistrationFee(gridWithPrice.registrationFee);
      }
    }

    // Initialize combined prices (niveau × type) - prefer grids with actual prices
    const initialCombined: Record<string, { tuition: number; registration: number }> = {};
    for (let i = 1; i <= selectedFiliere.nombreAnnees; i++) {
      // For each niveau and type combination, find the grid with actual price
      const initialeGrids = gridsToUse.filter((g) => g.niveau === i && g.typeFormation === 'INITIALE');
      const continueGrids = gridsToUse.filter((g) => g.niveau === i && g.typeFormation === 'CONTINUE');

      const initialeGrid = initialeGrids.find(g => g.tuitionAmount > 0 || g.registrationFee > 0) || initialeGrids[0];
      const continueGrid = continueGrids.find(g => g.tuitionAmount > 0 || g.registrationFee > 0) || continueGrids[0];

      initialCombined[`${i}-INITIALE`] = {
        tuition: initialeGrid?.tuitionAmount || 0,
        registration: initialeGrid?.registrationFee || 0,
      };
      initialCombined[`${i}-CONTINUE`] = {
        tuition: continueGrid?.tuitionAmount || 0,
        registration: continueGrid?.registrationFee || 0,
      };
    }
    setCombinedPrices(initialCombined);
  }, [selectedFiliere, existingGrids, differsByCampus, selectedCampusId]);

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const collectPriceValues = (): number[] => {
    const values: number[] = [tuitionAmount, registrationFee];
    Object.values(combinedPrices).forEach((p) => values.push(p.tuition, p.registration));
    Object.values(niveauPrices).forEach((p) => values.push(p.tuition, p.registration));
    Object.values(typePrices).forEach((p) => values.push(p.tuition, p.registration));
    return values;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // The submit button sits outside the <form>, so native required/min
    // attributes never fire — validate amounts here.
    const priceValues = collectPriceValues();
    if (priceValues.some((v) => Number.isNaN(v) || v < 0)) {
      setSubmitError('Les montants doivent être des nombres positifs ou nuls');
      return;
    }

    try {
      if (isSingleGridEdit && grid) {
        // Update single existing grid (e.g., Formation Initiale only)
        await updateMutation.mutateAsync({
          id: grid.id,
          data: {
            tuitionAmount,
            registrationFee,
          },
        });
      } else {
        // Bulk create/update - handles both new configuration and bulk edit mode
        // Build bulk create data based on global settings
        const data: BulkCreateTuitionGridData = {
          filiereId,
          samePriceAllNiveaux: !differsByNiveau,
          samePriceAllCampuses: !differsByCampus, // true = same price for all campuses, false = per-campus
          samePriceAllTypes: !differsByTypeFormation,
        };

        // Determine which price mode to use based on global settings
        const allSame = !differsByNiveau && !differsByTypeFormation;
        const bothVary = differsByNiveau && differsByTypeFormation;

        if (allSame && !differsByCampus) {
          // Simple case: same price everywhere, no campus differentiation
          data.tuitionAmount = tuitionAmount;
          data.registrationFee = registrationFee;
        } else if (allSame && differsByCampus && selectedCampusId) {
          // Same price for all niveaux/types but specific to this campus
          // Build combined prices for this campus
          const combined: CombinedPrice[] = [];
          const allNiveaux = niveauOptions.length > 0 ? niveauOptions : [1];
          const allTypes: TypeFormation[] = ['INITIALE', 'CONTINUE'];

          for (const niveau of allNiveaux) {
            for (const typeFormation of allTypes) {
              combined.push({
                niveau,
                typeFormation,
                tuitionAmount,
                registrationFee,
                campusId: selectedCampusId,
              });
            }
          }
          data.combinedPrices = combined;
        } else {
          // Build combined prices for ALL niveaux and ALL types
          // Even when a dimension doesn't vary, we need to send all combinations
          // so the backend updates all grids correctly
          const combined: CombinedPrice[] = [];
          // Always iterate through ALL niveaux
          const allNiveaux = niveauOptions.length > 0 ? niveauOptions : [1];
          // Always include BOTH formation types
          const allTypes: TypeFormation[] = ['INITIALE', 'CONTINUE'];

          for (const niveau of allNiveaux) {
            for (const typeFormation of allTypes) {
              let finalTuition = tuitionAmount;
              let finalRegistration = registrationFee;

              if (bothVary) {
                // BOTH niveau and type vary - use combined prices matrix
                const key = `${niveau}-${typeFormation}`;
                if (combinedPrices[key]) {
                  finalTuition = combinedPrices[key].tuition;
                  finalRegistration = combinedPrices[key].registration;
                }
              } else if (differsByNiveau && !differsByTypeFormation) {
                // Only niveau varies - same price for both types at this niveau
                if (niveauPrices[niveau]) {
                  finalTuition = niveauPrices[niveau].tuition;
                  finalRegistration = niveauPrices[niveau].registration;
                }
              } else if (differsByTypeFormation && !differsByNiveau) {
                // Only type varies - same price for all niveaux of this type
                if (typePrices[typeFormation]) {
                  finalTuition = typePrices[typeFormation].tuition;
                  finalRegistration = typePrices[typeFormation].registration;
                }
              }

              combined.push({
                niveau,
                typeFormation,
                tuitionAmount: finalTuition,
                registrationFee: finalRegistration,
                // Include campusId when differsByCampus is enabled
                ...(differsByCampus && selectedCampusId ? { campusId: selectedCampusId } : {}),
              });
            }
          }

          data.combinedPrices = combined;
        }

        await createBulkMutation.mutateAsync(data);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement de la grille tarifaire");
    }
  };

  const isLoading = isLoadingFilieres || isLoadingCampusInfo;
  const isSubmitting = createBulkMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {isSingleGridEdit ? 'Modifier la grille' : isBulkEditMode ? 'Modifier les tarifs' : 'Configurer les tarifs'}
                  </h2>
                  {/* Show which type is being edited when editing a single grid */}
                  {isSingleGridEdit && grid && (
                    <p className="text-sm text-gray-500">
                      {grid.typeFormation === 'INITIALE' ? 'Formation Initiale' : 'Formation Continue'}
                      {grid.niveau > 0 && ` - ${grid.niveau === 1 ? '1ère' : `${grid.niveau}ème`} année`}
                    </p>
                  )}
                  {/* Show info when bulk editing existing grids */}
                  {isBulkEditMode && (
                    <p className="text-sm text-gray-500">
                      Modification de toutes les configurations
                    </p>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Form content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <>
                {/* Filière info - show as read-only when pre-selected or editing a specific grid */}
                {hasPreselectedFiliere || grid ? (
                  <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-primary-900">
                          {preselectedFiliereName || grid?.filiere?.name || selectedFiliere?.filiere || 'Filière sélectionnée'}
                        </p>
                        <p className="text-sm text-primary-600">
                          {grid?.filiere?.code || selectedFiliere?.codeFiliere || ''}{grid?.filiere?.code || selectedFiliere?.codeFiliere ? ' - ' : ''}{grid?.filiere?.nombreAnnees || selectedFiliere?.nombreAnnees || ''} an(s)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filière <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={filiereId}
                      onChange={(e) => setFiliereId(e.target.value)}
                      disabled={isEditMode}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                      required
                    >
                      <option value="">Sélectionner une filière</option>
                      {filieres?.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.filiere} ({f.codeFiliere}) - {f.nombreAnnees} an(s)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Campus selector - shown when differsByCampus is enabled and multiple campuses exist */}
                {differsByCampus && campusInfo?.hasMultipleCampuses && !isSingleGridEdit && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">Campus</p>
                        <p className="text-sm text-blue-600">Sélectionnez le campus pour cette configuration</p>
                      </div>
                    </div>
                    <select
                      value={selectedCampusId || ''}
                      onChange={(e) => setSelectedCampusId(e.target.value || undefined)}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      required
                    >
                      <option value="">Sélectionner un campus</option>
                      {campusInfo?.campuses.map((campus) => (
                        <option key={campus.id} value={campus.id}>
                          {campus.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Price inputs - based on global settings */}
                {/* Require campus selection when differsByCampus is enabled */}
                {(filiereId || isEditMode) && (!differsByCampus || !campusInfo?.hasMultipleCampuses || selectedCampusId || isSingleGridEdit) && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Tarification</h4>

                    {/* Simple case: no dimensions vary OR single grid edit mode */}
                    {/* When bulk editing (parent row), respect the settings to show all options */}
                    {(!differsByNiveau && !differsByTypeFormation) || isSingleGridEdit ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Frais d'inscription (MAD)
                          </label>
                          <input
                            type="number"
                            value={registrationFee}
                            onChange={(e) => setRegistrationFee(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            min="0"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Frais de scolarité (MAD)
                          </label>
                          <input
                            type="number"
                            value={tuitionAmount}
                            onChange={(e) => setTuitionAmount(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            min="0"
                            required
                          />
                        </div>
                      </div>
                    ) : differsByNiveau && differsByTypeFormation ? (
                      // BOTH dimensions vary - show matrix of niveau × type
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-sm text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg">
                          <Layers className="w-4 h-4" />
                          <span>Tarification par niveau et type de formation</span>
                        </div>
                        {niveauOptions.map((niveau) => (
                          <div key={niveau} className="space-y-2">
                            <h5 className="text-sm font-semibold text-gray-800 bg-gray-100 px-3 py-2 rounded-lg">
                              {niveau === 1 ? '1ère' : `${niveau}ème`} année
                            </h5>
                            {(['INITIALE', 'CONTINUE'] as const).map((type) => {
                              const key = `${niveau}-${type}`;
                              return (
                                <div
                                  key={key}
                                  className="grid grid-cols-5 gap-3 items-center bg-gray-50 p-3 rounded-lg ml-4"
                                >
                                  <span className={`text-sm font-medium ${type === 'INITIALE' ? 'text-blue-700' : 'text-green-700'}`}>
                                    {type === 'INITIALE' ? 'Initiale' : 'Continue'}
                                  </span>
                                  <div className="col-span-2">
                                    <input
                                      type="number"
                                      placeholder="Inscription"
                                      value={combinedPrices[key]?.registration || ''}
                                      onChange={(e) =>
                                        setCombinedPrices((prev) => ({
                                          ...prev,
                                          [key]: {
                                            ...prev[key],
                                            registration: Number(e.target.value),
                                          },
                                        }))
                                      }
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      min="0"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <input
                                      type="number"
                                      placeholder="Scolarité"
                                      value={combinedPrices[key]?.tuition || ''}
                                      onChange={(e) =>
                                        setCombinedPrices((prev) => ({
                                          ...prev,
                                          [key]: {
                                            ...prev[key],
                                            tuition: Number(e.target.value),
                                          },
                                        }))
                                      }
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      min="0"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : differsByNiveau ? (
                      // Only niveau varies
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg">
                          <Layers className="w-4 h-4" />
                          <span>Tarification par niveau (configurée dans Paramètres)</span>
                        </div>
                        {niveauOptions.map((niveau) => (
                          <div
                            key={niveau}
                            className="grid grid-cols-5 gap-3 items-center bg-gray-50 p-3 rounded-lg"
                          >
                            <span className="text-sm font-medium text-gray-700">
                              {niveau === 1 ? '1ère' : `${niveau}ème`} année
                            </span>
                            <div className="col-span-2">
                              <input
                                type="number"
                                placeholder="Inscription"
                                value={niveauPrices[niveau]?.registration || ''}
                                onChange={(e) =>
                                  setNiveauPrices((prev) => ({
                                    ...prev,
                                    [niveau]: {
                                      ...prev[niveau],
                                      registration: Number(e.target.value),
                                    },
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                min="0"
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="number"
                                placeholder="Scolarité"
                                value={niveauPrices[niveau]?.tuition || ''}
                                onChange={(e) =>
                                  setNiveauPrices((prev) => ({
                                    ...prev,
                                    [niveau]: {
                                      ...prev[niveau],
                                      tuition: Number(e.target.value),
                                    },
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                min="0"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : differsByTypeFormation ? (
                      // Only type varies
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                          <GraduationCap className="w-4 h-4" />
                          <span>Tarification par type de formation (configurée dans Paramètres)</span>
                        </div>
                        {(['INITIALE', 'CONTINUE'] as const).map((type) => (
                          <div
                            key={type}
                            className="grid grid-cols-5 gap-3 items-center bg-gray-50 p-3 rounded-lg"
                          >
                            <span className="text-sm font-medium text-gray-700">
                              {type === 'INITIALE' ? 'Initiale' : 'Continue'}
                            </span>
                            <div className="col-span-2">
                              <input
                                type="number"
                                placeholder="Inscription"
                                value={typePrices[type]?.registration || ''}
                                onChange={(e) =>
                                  setTypePrices((prev) => ({
                                    ...prev,
                                    [type]: {
                                      ...prev[type],
                                      registration: Number(e.target.value),
                                    },
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                min="0"
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="number"
                                placeholder="Scolarité"
                                value={typePrices[type]?.tuition || ''}
                                onChange={(e) =>
                                  setTypePrices((prev) => ({
                                    ...prev,
                                    [type]: {
                                      ...prev[type],
                                      tuition: Number(e.target.value),
                                    },
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                min="0"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </form>

          {/* Footer */}
          {submitError && (
            <div className="mx-6 mb-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          )}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !filiereId || (differsByCampus && campusInfo?.hasMultipleCampuses && !selectedCampusId && !isSingleGridEdit)}
              onClick={handleSubmit}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEditMode ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TuitionGridFormModal;
